import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import { PieceRateService } from 'projects/warehousing/src/app/services/piece-rate.service';
import { Subscription } from 'rxjs';
import { SiteConfig } from 'projects/shared/src/lib/models/site-config';
import { UntypedFormControl } from '@angular/forms';
import { timeFormat } from 'projects/shared/src/lib/helpers/timeFormat';
import { PieceRateAdjustment } from '@shared/models/piece-rate-adjustment';
import { PieceRateActivity } from '@shared/models/piece-rate-activity';
import {DateTime} from 'luxon';
import { MessageService } from '@shared/services/message-service';
import { PieceRateSiteWeek } from '@shared/models/piece-rate-site-week';

@Component({
    selector: 'app-piece-rate-adjustment-calculation',
    templateUrl: './piece-rate-adjustment-calculation.component.html',
    styleUrls: ['./piece-rate-adjustment-calculation.component.scss']
})
export class PieceRateAdjustmentCalculationComponent
    implements OnInit, OnDestroy {

    @Input() showAdjustmentOption = false;

    subscriptions: Subscription[];
    siteId: string;
    siteWeek: PieceRateSiteWeek;

    combShiftPrevValue: string | null = null;
    combShiftControl = new UntypedFormControl(null);
    combShiftConfirmVisible = false;
    changingAdjCalculation = false;
    defaultNptSiteConfig: SiteConfig;
    defaultNPT = new UntypedFormControl('00:00');
    showNonProductiveHours: boolean;
    saturdayDate: DateTime;

    _activity: PieceRateActivity;

    @Input() set activity(act: PieceRateActivity) {
        this._activity = act;
        this.showNptForSelectedActivity();
    }

    adjustment: PieceRateAdjustment;

    constructor( private shiftContextService: ShiftContextService, private pieceRateService: PieceRateService, private messageService: MessageService) { }

    ngOnInit(): void {
        this.changingAdjCalculation = false;

        this.subscriptions = [
            this.shiftContextService.ImmediateSelection.subscribe(context => {
                this.siteId = context.siteId;
                this.generateNextSaturdayDate(context.date);
                this.pieceRateService.CurrentSiteWeek.subscribe(week => {
                    this.siteWeek = week;
                    if (this.siteWeek) {
                        this.combShiftPrevValue = this.siteWeek.combinedShifts ? 'combinedShift' : 'separateShift';
                        this.combShiftControl.setValue(this.combShiftPrevValue, { emitEvent: false });
                    }
                });
            }),
            this.shiftContextService.SiteConfig.subscribe(config => {
                this.defaultNptSiteConfig = config.find(sc => sc.configKey === SiteConfig.PIECERATE_NPT_DEFAULT);
                if (this.defaultNptSiteConfig) {
                    this.defaultNPT.setValue(this.defaultNptSiteConfig.configValue);
                } else {
                    this.defaultNPT.setValue('00:00');
                }
            }),
            this.pieceRateService.CurrentPieceRateAdjustment.subscribe(buildersPieceRateAdjustment => {
                this.adjustment = buildersPieceRateAdjustment;
                this.showNptForSelectedActivity();
            }),

            this.combShiftControl.valueChanges.subscribe(combShift => {
                this.combShiftConfirmVisible = true;
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.combShiftControl.setValue(null, { emitEvent: false });
    }

    cancelAdjustmentCalculation(): void {
        this.combShiftControl.setValue(this.combShiftPrevValue, { emitEvent: false });
        this.combShiftConfirmVisible = false;
    }

    confirmAdjustmentCalculation(): void {
        // working indication
        this.changingAdjCalculation = true;
        this.combShiftControl.disable();

        this.pieceRateService.postSiteWeekCombinedShifts(this.siteId, this.saturdayDate, this.combShiftControl.value === 'combinedShift')
            .subscribe({
                next: _ => {
                    this.combShiftPrevValue = this.combShiftControl.value;

                    // working indication
                    this.changingAdjCalculation = false;
                    this.combShiftControl.enable();

                    this.combShiftConfirmVisible = false;
                },
                error: _err => {
                    // working indication
                    this.changingAdjCalculation = false;
                    this.combShiftControl.enable();
                }
            });
    }

    onChangeDefaultNpt(): void {
        const convertedTime = timeFormat(this.defaultNPT.value);
        this.defaultNPT.setValue(convertedTime);
        this.shiftContextService.SetConfig(SiteConfig.PIECERATE_NPT_DEFAULT, this.defaultNPT.value).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Updated Default NPT',
                    detail: 'The default NPT allotted per builder has been updated.'
                });
            },
            error: _ => {}
        });
    }

    showNptForSelectedActivity(): void {
        const adjTypeId: number = this._activity.id;

        if (this.adjustment) {
            const config = this.adjustment.activityConfigs.find(ae => ae.pieceRateActivity === adjTypeId);
            if (config)
                this.showNonProductiveHours = config.showNonProductiveHours;
        }
    }

    generateNextSaturdayDate(currentDate: DateTime): void {
        switch (currentDate.weekday) {
            case 7: // sunday
                this.saturdayDate = currentDate.plus({days: 6});
                break;
            default:
                this.saturdayDate = currentDate.plus({days: 6 - currentDate.weekday});
        }
    }
}
